import { Link } from 'react-router-dom'
import { solutions } from '@/data/solutions'
import { functions } from '@/data/functions'
import { SourceForgeBadge } from './SourceForgeBadge'

/**
 * Shared marketing footer.
 *
 * Was previously duplicated in 13 places — Landing had a rich 5-column
 * footer, every other route had a one-line strip with 4 links. Made
 * the site feel asymmetric and missed obvious internal-linking wins
 * (Compare matrix, ROI calculator, glossary, industry pages — none of
 * those reachable from a /blog/<slug> footer). This component
 * centralises Landing's richer pattern so every marketing page links
 * to every other marketing page.
 *
 * Note: kept as a function component (not a const arrow) so it shows
 * up in React DevTools with a clean name.
 */
export function MarketingFooter() {
  const year = new Date().getFullYear()
  return (
    <footer className="bg-gray-900 text-gray-400 py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-10">
          {/* Brand column */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <img
                src="/algoscale-logo.png"
                alt="Algoscale company logo"
                width="172"
                height="49"
                loading="lazy"
                decoding="async"
                className="h-7 w-auto brightness-200"
              />
              <span className="text-base font-semibold text-white">DocumentIQ</span>
            </div>
            <p className="text-sm text-gray-500">
              AI-powered document intelligence by{' '}
              <a
                href="https://algoscale.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white transition-colors"
              >
                Algoscale
              </a>
            </p>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">Product</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/solutions" className="hover:text-white transition-colors">Solutions</Link></li>
              <li><Link to="/case-studies" className="hover:text-white transition-colors">Case Studies</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/extract" className="hover:text-white transition-colors">Document Types</Link></li>
              <li><Link to="/roi-calculator" className="hover:text-white transition-colors">ROI Calculator</Link></li>
              <li><Link to="/glossary" className="hover:text-white transition-colors">Glossary</Link></li>
            </ul>
          </div>

          {/* By Function — same source of truth as the header dropdown */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">By Function</h4>
            <ul className="space-y-2 text-sm">
              {functions.map((fn) => (
                <li key={fn.slug}>
                  <Link
                    to={`/solutions/function/${fn.slug}`}
                    className="hover:text-white transition-colors"
                  >
                    {fn.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* By Industry */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">By Industry</h4>
            <ul className="space-y-2 text-sm">
              {solutions.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/solutions/industry/${s.slug}`}
                    className="hover:text-white transition-colors"
                  >
                    {s.industry}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Compare */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">Compare</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/compare/documentiq-vs-abbyy" className="hover:text-white transition-colors">vs ABBYY</Link></li>
              <li><Link to="/compare/documentiq-vs-textract" className="hover:text-white transition-colors">vs AWS Textract</Link></li>
              <li><Link to="/compare/documentiq-vs-azure-doc-intelligence" className="hover:text-white transition-colors">vs Azure Doc Intelligence</Link></li>
              <li><Link to="/compare/documentiq-vs-rossum" className="hover:text-white transition-colors">vs Rossum</Link></li>
              <li><Link to="/compare/documentiq-vs-nanonets" className="hover:text-white transition-colors">vs Nanonets</Link></li>
              <li><Link to="/compare/documentiq-vs-docsumo" className="hover:text-white transition-colors">vs Docsumo</Link></li>
              <li><Link to="/compare/documentiq-vs-contract-review" className="hover:text-white transition-colors">vs Contract Review</Link></li>
              <li><Link to="/compare/documentiq-vs-manual" className="hover:text-white transition-colors">vs Manual Entry</Link></li>
            </ul>
          </div>

          {/* Company / Algoscale */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="https://algoscale.com/about-algoscale-team/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">About Algoscale</a></li>
              <li><a href="https://algoscale.com/ai-consulting-service/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">AI Consulting</a></li>
              <li><a href="https://algoscale.com/generative-ai-services/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Generative AI</a></li>
              <li><a href="https://algoscale.com/data-engineering-services/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Data Engineering</a></li>
              <li><a href="mailto:askus@algoscale.com" className="hover:text-white transition-colors">Contact Sales</a></li>
            </ul>
          </div>
        </div>

        {/* Awards / trust badges row — SourceForge + Slashdot + TBS.
            Each badge is mutated client-side by SF's loader script; the
            prerendered HTML carries only the empty containers. */}
        <div className="pt-8 border-t border-gray-800 flex flex-wrap items-center justify-center gap-6 mb-6">
          <SourceForgeBadge
            variantId="sf"
            badge="customers-love-us-white"
            href="https://sourceforge.net/software/product/DocumentIQ/"
          />
          <SourceForgeBadge
            variantId="sd"
            badge="users-love-us-new-white"
            href="https://slashdot.org/software/p/DocumentIQ/"
          />
          <SourceForgeBadge
            variantId="tbs"
            badge="most-loved"
            href="https://topbusinesssoftware.com/products/DocumentIQ/reviews/"
          />
        </div>

        <div className="pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {year} Algoscale. All rights reserved.</p>
          <div className="flex items-center gap-4 text-sm">
            <a href="https://algoscale.com/analyst-iq/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">AnalystIQ</a>
            <a href="https://algoscale.com/voice-ai-agent/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Voice AI Agent</a>
            <a href="https://algoscale.com/chat-ai-agent/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Chat AI Agent</a>
          </div>
        </div>
      </div>
    </footer>
  )
}
